import { useState, useEffect, useRef } from "react";
import { Offcanvas, Row, Col, Form, Button } from "react-bootstrap";
import useFetchData from "./useFetchDatav2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useToast } from "../Context/useToast";
import InitialsAvatar from "../components/InitialsAvatar";

import { FaNotEqual, FaEquals, FaTimes } from "react-icons/fa";
import { format, parseISO } from "date-fns";
import CurrencyInput from "./CurrencyInput";
import "./ReconcileInvoiceStatementPdfModal.css";
import "./ReconcileInvoiceStatementAuditModal.css";

const ReconcileInvoiceStatementAuditModal = ({ filteredRecon, onCloseModal }) => {
   const [isLoading, setIsLoading] = useState(true);
   const [payloadAudits, setPayloadAudits] = useState([]);
   const [sdId, setSDId] = useState(filteredRecon?.sd_id || "");
   const [enId, setEntryId] = useState(filteredRecon?.en_id || "");
   const [matchStatus, setMatchStatus] = useState(filteredRecon?.match_status || null);
   const { triggerToast } = useToast();

   const supplierInvoiceNumberRef = useRef("");
   const supplierPONumberRef = useRef("");
   const externalInvoiceNumberRef = useRef("");
   const externalPONumberRef = useRef("");
   const supplierInvoiceAmountRef = useRef("");
   const externalInvoiceAmountRef = useRef("");
   const ignoreSupplierRef = useRef("");
   const ignoreEntryRef = useRef("");

   const [supplierInvoiceDate, setSupplierInvoiceDate] = useState(null);
   const [externalInvoiceDate, setExternalInvoiceDate] = useState(null);
   const supplierInvoiceDateRef = useRef("");
   const externalInvoiceDateRef = useRef("");
   const offcanvasRef = useRef(null); //Ref to the Offcanvas
   const headerColWidthRef = useRef(null);
   const [show, setShow] = useState(true);
   const { fetchData } = useFetchData();

   //For invoice input elements
   const readOnlyStyle = {
      backgroundColor: "#fbfcfe",
      cursor: "not-allowed",
   };

   useEffect(() => {
      if (filteredRecon) {
         const data = filteredRecon;

         setMatchStatus(data.match_status);
         supplierInvoiceNumberRef.current = data.sd_parsed_invoice_no || "";
         supplierPONumberRef.current = data.sd_po_no || "";
         externalInvoiceNumberRef.current = data.esh_parsed_invoice_id || "";
         externalPONumberRef.current = data.en_pono || "";
         supplierInvoiceAmountRef.current = data.sd_net_amount?.toFixed(2) ?? "";
         externalInvoiceAmountRef.current = data.en_invoice_sub_amount?.toFixed(2) ?? "";
         ignoreSupplierRef.current = data.sd_ignored || false;
         ignoreEntryRef.current = data.en_ignore || false;
         setSupplierInvoiceDate(data.sd_invoice_date);
         setExternalInvoiceDate(data.en_invoice_date);
         supplierInvoiceDateRef.current = data.sd_invoice_date || "";
         externalInvoiceDateRef.current = data.en_invoice_date || "";
      }
   }, [filteredRecon]);

   // Fetch existing follow-ups (if any)
   useEffect(() => {
      const getAudits = async () => {
         if (!sdId && !enId) return;
         setIsLoading(true);
         const url = "/getAudits.sz";
         const method = "POST";
         const formData = new FormData();
         formData.append("_sd_id", sdId);
         formData.append("_esh_id", enId);
         try {
            const { responseData, fetchLoading, fetchError } = await fetchData(url, method, formData, "form-data");
            if (responseData.st === 1) {
               setPayloadAudits(responseData?.payload);
            } else {
               setPayloadAudits([]);
               if (responseData.m_en) {
                  triggerToast("warning", responseData.m_en, false, "", false);
               } else {
                  triggerToast("error", "We seem to have an issue retrieving Auditss, please try again later.", true, "Issue", false, 0);
               }
            }
         } catch (error) {
            setPayloadAudits([]);
            triggerToast("error", "We seem to have an access Audits, please try again later.", true, "Network Issue", false, 0);
         } finally {
            setIsLoading(false);
         }
      };
      getAudits();
   }, [sdId, enId]);

   const onClose = (e) => {
      setShow(false);
      onCloseModal?.();
   };

   // Parses YYYY-MM-DD into a Date object
   const parseDate = (input) => {
      const parts = input.split("-");
      if (parts.length === 3) {
         const date = new Date(parts[0], parts[1] - 1, parts[2]);
         return isNaN(date.getTime()) ? null : date;
      }
      return null;
   };

   // Formats Date object into YYYY-MM-DD string
   const formatDate = (date) => {
      return date instanceof Date && !isNaN(date) ? date.toISOString().split("T")[0] : "";
   };

   const InvoiceForm = () => {
      const totalPages = 2;
      const grade = "C";
      return (
         <div className="d-flex">
            <div className="d-flex flex-grow-1 justify-content-center">
               <div className="d-flex  align-items-center">
                  <div className="container audit-form">
                     <Form>
                        <Row className="d-flex flex-nowrap h-100">
                           <Col className="flex-fill">
                              <Row className="align-items-center mb-3">
                                 <div className="rec-inv-stat-pdf-col label mt-4" style={{ width: "90px", flex: "none" }}>
                                    <span className="label-text d-flex justify-content-end text-end flex-wrap me-2">
                                       Supplier
                                       <br />
                                       Statement
                                    </span>
                                 </div>
                                 <div className="rec-inv-stat-pdf-col input" style={{ width: "155px", flex: "none" }}>
                                    <Form.Group controlId="supplierInvoiceNumber">
                                       <Form.Label>Invoice Number</Form.Label>
                                       <Form.Control className="mt-2" type="text" placeholder="Invoice Number" defaultValue={supplierInvoiceNumberRef.current} readOnly style={readOnlyStyle} />
                                    </Form.Group>
                                 </div>
                                 <div className="rec-inv-stat-pdf-col input" style={{ width: "155px", flex: "none" }}>
                                    <Form.Group controlId="supplierPONumber">
                                       <Form.Label>P/O Number</Form.Label>
                                       <Form.Control className="mt-2" type="text" placeholder="P/O" defaultValue={supplierPONumberRef.current} readOnly style={readOnlyStyle} />
                                    </Form.Group>
                                 </div>
                                 <div className="rec-inv-stat-pdf-col input" style={{ width: "155px", flex: "none" }}>
                                    <Form.Group controlId="supplierInvoiceDate">
                                       <Form.Label>Invoice Date</Form.Label>
                                       <DatePicker
                                          selected={supplierInvoiceDate}
                                          readOnly
                                          style={readOnlyStyle}
                                          dateFormat="yyyy-MM-dd"
                                          placeholderText="YYYY-MM-DD"
                                          customInput={
                                             <Form.Control
                                                type="text"
                                                //value={supplierInvoiceDateRef.current}
                                                placeholder="YYYY-MM-DD"
                                                className="mt-2"
                                             />
                                          }
                                       />
                                    </Form.Group>
                                 </div>
                                 <div className="rec-inv-stat-pdf-col input pe-0" style={{ width: "155px", flex: "none" }}>
                                    <Form.Group controlId="supplierInvoiceAmount">
                                       <Form.Label>Invoice Amount</Form.Label>
                                       <CurrencyInput value={supplierInvoiceAmountRef.current} readOnly style={readOnlyStyle} className="mt-2" />
                                    </Form.Group>
                                 </div>
                                 <div className="rec-inv-stat-pdf-col connector d-flex align-items-start">
                                    <div className="horizontal-line" style={{ marginTop: "15px" }}></div>
                                    <div className="vertical-top-line"></div>
                                 </div>
                              </Row>
                              <Row className="align-items-center">
                                 <div className="rec-inv-stat-pdf-col d-flex justify-content-end text-end label" style={{ width: "90px", flex: "none" }}>
                                    <span className="label-text flex-wrap">
                                       Protractor
                                       <br />
                                       Entry
                                    </span>
                                 </div>
                                 <div className="rec-inv-stat-pdf-col input" style={{ width: "155px", flex: "none" }}>
                                    <Form.Group controlId="entryInvoiceNumber">
                                       <Form.Control type="text" className="mt-2" placeholder="Invoice Number" defaultValue={externalInvoiceNumberRef.current} readOnly style={readOnlyStyle} />
                                    </Form.Group>
                                 </div>
                                 <div className="rec-inv-stat-pdf-col input" style={{ width: "155px", flex: "none" }}>
                                    <Form.Group controlId="entryPONumber">
                                       <Form.Control type="text" className="mt-2" placeholder="P/O Number" defaultValue={externalPONumberRef.current} readOnly style={readOnlyStyle} />
                                    </Form.Group>
                                 </div>
                                 <div className="rec-inv-stat-pdf-col input" style={{ width: "155px", flex: "none" }}>
                                    <Form.Group controlId="entryInvoiceDate">
                                       <DatePicker selected={externalInvoiceDate} dateFormat="yyyy-MM-dd" placeholderText="YYYY-MM-DD" disabled={true} customInput={<Form.Control type="text" value={externalInvoiceDate} placeholder="YYYY-MM-DD" className="mt-2" style={readOnlyStyle} readOnly={true} />} />
                                    </Form.Group>
                                 </div>
                                 <div className="rec-inv-stat-pdf-col input pe-0" style={{ width: "155px", flex: "none" }}>
                                    <Form.Group controlId="entryInvoiceAmount">
                                       <CurrencyInput value={externalInvoiceAmountRef.current} className="mt-2" readOnly={true} />
                                    </Form.Group>
                                 </div>
                                 <div className="rec-inv-stat-pdf-col connector d-flex align-items-start">
                                    <div className="horizontal-line" style={{ marginTop: "0px" }}></div>

                                    {ignoreEntryRef.current || ignoreSupplierRef.current ? null : matchStatus === "MATCHED" || matchStatus === "FULL_MATCH" ? <FaEquals size={16} color="green" className="icon-connector" /> : matchStatus === "PARTIAL" ? <FaNotEqual size={16} color="orange" className="icon-connector" /> : matchStatus === "UNMATCHED" ? <FaTimes size={16} color="red" className="icon-connector" /> : matchStatus === "NO_ERP" ? <FaTimes size={16} color="red" className="icon-connector" /> : null}
                                    <div className="vertical-bottom-line"></div>
                                 </div>
                              </Row>
                           </Col>
                           <Col className="d-flex flex-column align-items-center ps-0 pe-0">
                              <Row>
                                 <Form.Label>Ignore</Form.Label>
                              </Row>
                              <Row style={{ marginTop: "20px" }}>
                                 <Form.Check type="checkbox" id="ignoreSupplierInvoice" className="" defaultChecked={ignoreSupplierRef.current} disabled={true} />
                              </Row>
                              <Row></Row>
                              <Row style={{ marginTop: "30px" }}>
                                 <Form.Check type="checkbox" id="ignoreEntryInvoice" className="" defaultChecked={ignoreEntryRef.current} disabled={true} />
                              </Row>
                           </Col>
                        </Row>
                     </Form>
                  </div>
               </div>
            </div>
         </div>
      );
   };

   return (
      <>
         <Offcanvas
            style={{
               width: "825px",
               position: "fixed", // Keep fixed positioning
               top: 0,
               right: 0,
               height: "100vh",
               overflow: "hidden",
            }}
            ref={offcanvasRef}
            className=""
            onHide={onClose}
            show={show}
            scroll={true}
            backdrop={false}
            placement="end">
            <Offcanvas.Header className="me-3" closeButton>
               <Row className="d-flex align-items-center me-3 w-100">
                  <Col ref={headerColWidthRef} className="d-flex align-items-center justify-content-start gap-3">
                     <div className="d-flex align-items-center ms-1 text-muted">
                        {/* If we have no reconciliation, check supplier override, if not check protractor entry */}
                        {filteredRecon?.rc_id ? (
                           <>
                              <small className="text-muted">
                                 Reconciled On {format(parseISO(filteredRecon?.rc_date), "yyyy-MM-dd HH:mm")} by {filteredRecon?.rc_user_name}
                              </small>
                           </>
                        ) : filteredRecon?.sup_state_detail_ovr_update_on ? (
                           <>
                              <small className="text-muted me-1">
                                 Override Supplier On {format(parseISO(filteredRecon?.sup_state_detail_ovr_update_on), "yyyy-MM-dd HH:mm")} by {filteredRecon?.sd_user_name}
                              </small>
                           </>
                        ) : filteredRecon?.en_insert_on ? (
                           <>
                              <small className="text-muted me-1">
                                 Protractor Entry On {format(parseISO(filteredRecon?.en_insert_on), "yyyy-MM-dd HH:mm")} by {filteredRecon?.en_user_name}
                              </small>
                           </>
                        ) : (
                           ""
                        )}
                     </div>
                  </Col>
               </Row>
            </Offcanvas.Header>
            <Offcanvas.Body
               style={{
                  overflow: "hidden", // Prevent overflow on the Offcanvas.Body
                  paddingTop: "0px",
               }}>
               <Row>
                  <Col>
                     <div className="container" style={{ overflowX: "auto", whiteSpace: "nowrap", width: "100%", overflow: "visible" }}>
                        <InvoiceForm />
                     </div>
                  </Col>
               </Row>
               <hr />
               <Row>
                  <Col>
                     {/* <ul class="activity-group mb-5">
                        <li class="activity-date">Mar 12, 2025</li>
                        <li class="activity-item audit">
                           <div className="activity-audit">
                              <div className="activity-avatar-sm">
                                 <InitialsAvatar
                                    name={"Perry Palantzas"}
                                    className={"avatar-lg avatar-rounded-xl"}
                                 />
                              </div>
                           </div>
                           <p class="d-sm-flex align-items-center w-100 mb-2">
                              <span class="fs-sm flex-grow-1">
                                 <strong>Supplier Statement Scanned</strong> by <strong>Periklako Palantzas</strong>
                              </span>
                              <span class="text-secondary fs-xs ms-auto">21:28</span>
                           </p>
                        </li>
                        <li class="activity-date">Mar 13, 2025</li>
                        <li class="activity-item audit">
                           <p class="d-sm-flex align-items-center w-100 mb-0">
                              <span class="fs-sm">
                                 <strong>Invoice Entry Imported</strong>
                              </span>
                              <span class="fs-xs text-secondary ms-auto">13:44</span>
                           </p>
                        </li>
                        <li class="activity-item audit">
                           <p class="d-sm-flex align-items-center w-100 mb-0">
                              <span class="fs-sm">
                                 <strong>Reconciled</strong>
                              </span>
                              <span class="fs-xs text-secondary ms-auto">13:44</span>
                           </p>
                        </li>
                        <li class="activity-date">Mar 22, 2025</li>
                        <li class="activity-item audit">
                           <div className="activity-audit">
                              <div className="activity-avatar-sm">
                                 <InitialsAvatar
                                    name={"Perry Palantzas"}
                                    className={"avatar-lg avatar-rounded-xl"}
                                 />
                              </div>
                           </div>
                           <div class="activity-content w-100">
                              <div class="d-flex justify-content-between align-items-start">
                                 <div>
                                    <span class="fs-sm">
                                       <strong>Updated</strong> by <strong>Periklako Palantzas</strong>
                                    </span>
                                    <ul class="list-unstyled mb-0 mt-1">
                                       <li>
                                          Supplier PO Number <strong>163899</strong> was 16389.
                                       </li>
                                    </ul>
                                 </div>

                                 <span class="text-secondary fs-xs">09:08</span>
                              </div>
                           </div>
                        </li>
                     </ul> */}

                     <ul className="activity-group mb-5">
                        {payloadAudits.map((audit, index) => {
                           const date = audit.inserted_on ? format(parseISO(audit.inserted_on), "MMM dd, yyyy") : "";
                           const time = audit.inserted_on ? format(parseISO(audit.inserted_on), "HH:mm") : "";
                           const previousDate = index > 0 ? format(parseISO(payloadAudits[index - 1].inserted_on), "MMM dd, yyyy") : "";

                           const renderAuditChanges = () => {
                              if (!audit.audit_changes) return null;

                              return (
                                 <ul className="list-unstyled mb-0 mt-1">
                                    {Object.entries(audit.audit_changes).map(([field, { label, old, new: newVal }], i) => (
                                       <li key={i}>
                                          {label || field} <strong>{newVal}</strong> was {old}.
                                       </li>
                                    ))}
                                 </ul>
                              );
                           };

                           return (
                              <div key={index}>
                                 {date !== previousDate && (
                                    <li className="activity-date">
                                       <strong>{date}</strong>
                                    </li>
                                 )}

                                 <li className="activity-item audit">
                                    {audit.inserted_by && (
                                       <div className="activity-audit">
                                          <div className="activity-avatar-sm">
                                             <InitialsAvatar name={audit.inserted_by} className="avatar-sm avatar-rounded-xl" />
                                          </div>
                                       </div>
                                    )}

                                    <div className="activity-content w-100">
                                       <div className="d-flex justify-content-between align-items-start">
                                          <div>
                                             <span className="fs-sm">
                                                <span>{audit.label.replace(" On", "")}</span>
                                                {audit.inserted_by && (
                                                   <>
                                                      {" "}
                                                      by <span>{audit.inserted_by}</span>
                                                   </>
                                                )}
                                             </span>
                                             {renderAuditChanges()}
                                          </div>
                                          <span className="text-secondary fs-xs">{time}</span>
                                       </div>
                                    </div>
                                 </li>
                              </div>
                           );
                        })}
                     </ul>
                  </Col>
               </Row>
            </Offcanvas.Body>
         </Offcanvas>
      </>
   );
};

export default ReconcileInvoiceStatementAuditModal;
